import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  RefreshCw, Search, CheckCircle2, Clock, XCircle, CircleCheck, CircleX, Banknote,
  Filter, X, BadgeDollarSign,
} from "lucide-react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import usePagedRows from "../components/usePagedRows";
import Pagination from "../components/Pagination";
import { fmtBDT } from "../lib/format";

const STATUSES = [
  { v: "", label: "All" },
  { v: "pending", label: "Pending" },
  { v: "approved", label: "Approved" },
  { v: "paid", label: "Paid" },
  { v: "rejected", label: "Rejected" },
];

function StatusBadge({ status }) {
  const map = {
    paid: { color: "#10b981", icon: CheckCircle2 },
    approved: { color: "#3b82f6", icon: CircleCheck },
    pending: { color: "#fbbf24", icon: Clock },
    rejected: { color: "#e54b59", icon: XCircle },
  };
  const m = map[status] || { color: "#94a3b8", icon: Clock };
  const Icon = m.icon;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-widest" style={{ background: `${m.color}26`, border: `1px solid ${m.color}66`, color: m.color }}>
      <Icon size={11} /> {(status || "").toUpperCase()}
    </span>
  );
}

export default function AffiliateWithdraw() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, paid: 0, rejected: 0 });
  const [totalPaid, setTotalPaid] = useState(0);
  const [methods, setMethods] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [methodFilter, setMethodFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const paged = usePagedRows(rows, 12, [statusFilter, methodFilter, search]);

  const toggleSel = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };
  const bulk = async (action) => {
    if (selected.size === 0) return toast.error("Select at least one payout");
    let payload = { payout_ids: Array.from(selected), action };
    if (action === "mark_paid") {
      const tx = window.prompt("Common Tx Reference for all marked-paid?") || "";
      payload.tx_ref = tx;
    }
    if (action === "reject") {
      const reason = window.prompt("Rejection reason?") || "";
      payload.notes = reason;
    }
    try {
      const r = await api.post("/admin/aff-payouts/bulk", payload);
      toast.success(`Processed ${r.data?.processed || 0}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (methodFilter) params.method = methodFilter;
      if (search.trim()) params.user_query = search.trim();
      const { data } = await api.get("/admin/aff-payouts", { params });
      setRows(data.rows || []);
      setCounts(data.counts || {});
      setTotalPaid(data.total_paid || 0);
      setMethods(data.methods || []);
    } catch (e) {
      toast.error("Failed to load payouts");
    } finally { setLoading(false); }
  }, [statusFilter, methodFilter, search]);

  useEffect(() => { load(); }, [load]);

  const act = async (payout, action, payload) => {
    try {
      await api.patch(`/admin/aff-payouts/${payout.id}`, { action, ...(payload || {}) });
      toast.success(`Payout ${action === "mark_paid" ? "marked paid" : action + "d"}`);
      setModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div>
      <PageHeader title="Affiliate Withdraw" subtitle="Premium payout pipeline — review, approve, mark paid." />

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        <PremiumStat label="Pending" value={counts.pending || 0} color="#fbbf24" icon={Clock} />
        <PremiumStat label="Approved" value={counts.approved || 0} color="#3b82f6" icon={CircleCheck} />
        <PremiumStat label="Paid" value={counts.paid || 0} color="#10b981" icon={CheckCircle2} />
        <PremiumStat label="Rejected" value={counts.rejected || 0} color="#e54b59" icon={CircleX} />
        <PremiumStat label="Total Paid Out" value={`৳ ${fmtBDT(totalPaid)}`} color="#d4af37" icon={Banknote} highlight />
      </div>

      {/* Filters */}
      <div className="rounded-xl p-3 mb-4 flex flex-wrap items-center gap-2" style={{
        background: "linear-gradient(180deg, rgba(20,17,28,0.85), rgba(10,8,16,0.95))",
        border: "1px solid rgba(212,175,55,0.22)",
      }}>
        <div className="flex items-center gap-1 text-white/55 text-[11px] uppercase tracking-widest px-2" style={{ color: "rgba(212,175,55,0.7)" }}><Filter size={12} /> Status</div>
        {STATUSES.map((s) => (
          <button key={s.v} onClick={() => setStatusFilter(s.v)} className="px-3 h-8 rounded-md text-[12px] font-bold transition-all" style={statusFilter === s.v ? {
            background: "linear-gradient(135deg, rgba(212,175,55,0.22), rgba(160,125,46,0.08))",
            border: "1px solid rgba(212,175,55,0.5)",
            color: "#f4e9a1",
            boxShadow: "0 4px 12px rgba(212,175,55,0.15)",
          } : {
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
            color: "rgba(245,241,230,0.7)",
          }}>
            {s.label}
          </button>
        ))}
        <div className="flex-1 min-w-[160px]" />
        <select value={methodFilter} onChange={(e) => setMethodFilter(e.target.value)} className="h-8 px-2 rounded-md text-[12px] text-white" style={{ background: "rgba(0,0,0,0.3)", border: "1px solid rgba(212,175,55,0.25)" }}>
          <option value="">All methods</option>
          {methods.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <div className="flex items-center gap-2 h-8 px-2 rounded-md" style={{ background: "rgba(0,0,0,0.3)", border: "1px solid rgba(212,175,55,0.25)" }}>
          <Search size={12} className="text-amber-300/70" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="username / email / dest" className="bg-transparent outline-none text-[12px] w-44 text-white" />
        </div>
        <button onClick={load} className="h-8 px-3 rounded-md text-[12px] inline-flex items-center gap-1.5 font-semibold" style={{
          background: "rgba(212,175,55,0.10)", border: "1px solid rgba(212,175,55,0.35)", color: "#f4e9a1",
        }}><RefreshCw size={12} /> Refresh</button>
        {selected.size > 0 && (
          <>
            <div className="h-8 mx-2 w-px" style={{ background: "rgba(212,175,55,0.35)" }} />
            <span className="text-[12px] font-bold text-amber-200">{selected.size} selected</span>
            <button onClick={() => bulk("approve")} className="h-8 px-2.5 rounded-md text-[11px] font-bold" style={{ background: "rgba(59,130,246,0.16)", border: "1px solid rgba(59,130,246,0.45)", color: "#93c5fd" }} data-testid="bulk-approve">Bulk Approve</button>
            <button onClick={() => bulk("mark_paid")} className="h-8 px-2.5 rounded-md text-[11px] font-bold" style={{ background: "rgba(16,185,129,0.16)", border: "1px solid rgba(16,185,129,0.45)", color: "#6ee7b7" }} data-testid="bulk-mark-paid">Bulk Mark Paid</button>
            <button onClick={() => bulk("reject")} className="h-8 px-2.5 rounded-md text-[11px] font-bold" style={{ background: "rgba(229,75,89,0.16)", border: "1px solid rgba(229,75,89,0.45)", color: "#fda5b1" }} data-testid="bulk-reject">Bulk Reject</button>
          </>
        )}
      </div>

      {/* Table */}
      <div className="rounded-xl p-3 overflow-x-auto text-white" style={{
        background: "linear-gradient(180deg, rgba(20,17,28,0.85), rgba(10,8,16,0.95))",
        border: "1px solid rgba(212,175,55,0.22)",
      }}>
        {loading ? <div className="text-white/55 py-10 text-center">Loading…</div>
          : rows.length === 0 ? (
            <div className="text-white/55 py-12 text-center">No payout requests found</div>
          ) : (
            <table className="w-full text-sm min-w-[960px] text-white" data-testid="admin-payouts-table">
              <thead><tr className="text-left text-[11px] uppercase tracking-widest" style={{ color: "rgba(212,175,55,0.7)" }}>
                <th className="py-2 pr-2 w-7"><button onClick={toggleAll} className="text-amber-200/80 hover:text-amber-200">{selected.size === rows.length && rows.length > 0 ? "☑" : "☐"}</button></th>
                <th className="py-2 pr-2">Requested</th>
                <th className="py-2 pr-2">Affiliate</th>
                <th className="py-2 pr-2">Method</th>
                <th className="py-2 pr-2">Destination</th>
                <th className="py-2 pr-2 text-right">Amount</th>
                <th className="py-2 pr-2">Status</th>
                <th className="py-2 pr-2">Actions</th>
              </tr></thead>
              <tbody>
                {paged.visible.map((r) => (
                  <tr key={r.id} className="hover:bg-amber-500/5" style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                    <td className="py-2.5 pr-2"><button onClick={() => toggleSel(r.id)} className="text-amber-200/80 hover:text-amber-200" data-testid={`payout-sel-${r.id}`}>{selected.has(r.id) ? "☑" : "☐"}</button></td>
                    <td className="py-2.5 pr-2 text-white/80 text-xs">{(r.created_at || "").replace("T", " ").slice(0, 16)}</td>
                    <td className="py-2.5 pr-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-[10.5px] font-black shrink-0" style={{
                          background: "linear-gradient(135deg, #fff7d6, #d4af37 60%, #5a4615)",
                          color: "#1a1206",
                        }}>{(r.username || "?").slice(0, 2).toUpperCase()}</div>
                        <div>
                          <div className="font-bold">{r.username}</div>
                          <div className="text-[11px] text-white/55">{r.affiliate_email || "—"}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-2.5 pr-2">{r.method}</td>
                    <td className="py-2.5 pr-2 font-mono text-xs break-all max-w-[220px] text-amber-100/85">
                      {r.destination}
                      {r.holder_name && <div className="text-[11px] text-white/55 not-italic">{r.holder_name}</div>}
                    </td>
                    <td className="py-2.5 pr-2 text-right font-bold" style={{
                      background: "linear-gradient(135deg, #fff7d6 0%, #f4e9a1 30%, #d4af37 60%, #a07d2e 100%)",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      color: "transparent",
                    }}>৳ {fmtBDT(r.amount)}</td>
                    <td className="py-2.5 pr-2">
                      <StatusBadge status={r.status} />
                      {r.tx_ref && <div className="text-[10px] text-white/55 mt-0.5 font-mono">Tx: {r.tx_ref}</div>}
                    </td>
                    <td className="py-2.5 pr-2">
                      <ActionButtons payout={r} onAct={(a, p) => act(r, a, p)} onOpen={() => setModal({ payout: r })} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        <Pagination total={paged.totalRows} page={paged.page} pageSize={paged.pageSize} onChange={paged.setPage} testIdPrefix="aff-withdraw-pg" />
      </div>

      {modal && <PayoutModal payout={modal.payout} onClose={() => setModal(null)} onAct={(a, p) => act(modal.payout, a, p)} />}
    </div>
  );
}

function PremiumStat({ label, value, color, icon: Icon, highlight }) {
  return (
    <div className="rounded-xl p-4 relative overflow-hidden" style={{
      background: highlight
        ? `radial-gradient(400px 140px at 100% 0%, ${color}24, transparent 60%), linear-gradient(180deg, rgba(28,22,14,0.7), rgba(10,8,16,0.92))`
        : `radial-gradient(400px 120px at 0% 0%, ${color}18, transparent 60%), linear-gradient(180deg, rgba(20,17,28,0.85), rgba(10,8,16,0.95))`,
      border: `1px solid ${color}${highlight ? "55" : "3a"}`,
      boxShadow: highlight ? `0 18px 50px ${color}22, 0 0 0 1px ${color}30 inset` : "0 10px 24px rgba(0,0,0,0.35)",
    }}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/55 font-bold">{label}</div>
        {Icon && (
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}18`, border: `1px solid ${color}55`, color }}>
            <Icon size={14} />
          </div>
        )}
      </div>
      <div className="text-[22px] font-black" style={highlight ? {
        background: "linear-gradient(135deg, #fff7d6 0%, #f4e9a1 30%, #d4af37 60%, #a07d2e 100%)",
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        WebkitTextFillColor: "transparent",
        color: "transparent",
      } : { color }}>{value}</div>
    </div>
  );
}

function ActionButtons({ payout, onAct, onOpen }) {
  if (payout.status === "pending") {
    return (
      <div className="flex items-center gap-1">
        <button onClick={() => onAct("approve")} className="px-2 h-7 rounded-md text-xs font-bold border" style={{ background: "rgba(59,130,246,0.18)", borderColor: "rgba(59,130,246,0.45)", color: "#93c5fd" }} data-testid={`approve-${payout.id}`}>Approve</button>
        <button onClick={onOpen} className="px-2 h-7 rounded-md text-xs font-bold border" style={{ background: "rgba(16,185,129,0.18)", borderColor: "rgba(16,185,129,0.45)", color: "#6ee7b7" }} data-testid={`mark-paid-${payout.id}`}>Mark Paid</button>
        <button onClick={() => onAct("reject", { notes: window.prompt("Rejection reason?") || "" })} className="px-2 h-7 rounded-md text-xs font-bold border" style={{ background: "rgba(229,75,89,0.18)", borderColor: "rgba(229,75,89,0.45)", color: "#fda5b1" }} data-testid={`reject-${payout.id}`}>Reject</button>
      </div>
    );
  }
  if (payout.status === "approved") {
    return (
      <div className="flex items-center gap-1">
        <button onClick={onOpen} className="px-2 h-7 rounded-md text-xs font-bold border" style={{ background: "rgba(16,185,129,0.18)", borderColor: "rgba(16,185,129,0.45)", color: "#6ee7b7" }}>Mark Paid</button>
        <button onClick={() => onAct("reject", { notes: window.prompt("Rejection reason?") || "" })} className="px-2 h-7 rounded-md text-xs font-bold border" style={{ background: "rgba(229,75,89,0.18)", borderColor: "rgba(229,75,89,0.45)", color: "#fda5b1" }}>Reject</button>
      </div>
    );
  }
  return <span className="text-white/40 text-xs">—</span>;
}

function PayoutModal({ payout, onClose, onAct }) {
  const [txRef, setTxRef] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!txRef.trim()) { toast.error("Tx reference required"); return; }
    setBusy(true);
    await onAct("mark_paid", { tx_ref: txRef.trim(), notes: notes.trim() });
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "radial-gradient(ellipse at center, rgba(0,0,0,0.5), rgba(0,0,0,0.85))", backdropFilter: "blur(8px)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl p-6 text-white relative" style={{
        background: "linear-gradient(180deg, rgba(22,18,30,0.96), rgba(8,6,14,0.98))",
        border: "1px solid rgba(212,175,55,0.4)",
        boxShadow: "0 40px 100px rgba(0,0,0,0.7), 0 0 0 1px rgba(212,175,55,0.18) inset",
      }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[20px] font-black" style={{ background: "linear-gradient(180deg, #ffffff, #d2d7df)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent", color: "transparent" }}>Mark as Paid</h3>
          <button onClick={onClose} className="p-1.5 rounded-md bg-white/5 hover:bg-white/10"><X size={16} /></button>
        </div>
        <div className="rounded-xl p-3.5 mb-4" style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.12), transparent)", border: "1px solid rgba(212,175,55,0.3)" }}>
          <div className="grid grid-cols-2 gap-3 text-[12.5px]">
            <div><span className="text-white/50">Affiliate</span><div className="font-bold">{payout.username}</div></div>
            <div><span className="text-white/50">Method</span><div className="font-bold">{payout.method}</div></div>
            <div className="col-span-2"><span className="text-white/50">Destination</span><div className="font-mono text-amber-100 break-all">{payout.destination}</div></div>
            <div className="col-span-2"><span className="text-white/50">Amount</span><div className="font-black text-[20px]" style={{ background: "linear-gradient(135deg, #fff7d6 0%, #f4e9a1 30%, #d4af37 60%, #a07d2e 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent", color: "transparent" }}>৳ {fmtBDT(payout.amount)}</div></div>
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] font-bold mb-1.5" style={{ color: "rgba(212,175,55,0.75)" }}>Transaction Reference *</label>
            <input value={txRef} onChange={(e) => setTxRef(e.target.value)} className="w-full h-11 px-3 rounded-lg outline-none text-sm font-mono text-white" style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(212,175,55,0.3)" }} placeholder="Tx hash / order id" data-testid="modal-tx-ref" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] font-bold mb-1.5" style={{ color: "rgba(212,175,55,0.75)" }}>Notes (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full p-3 rounded-lg outline-none text-sm text-white" style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(212,175,55,0.3)" }} />
          </div>
          <button onClick={submit} disabled={busy} className="w-full h-12 rounded-xl font-black uppercase tracking-wider disabled:opacity-60" style={{
            background: "linear-gradient(180deg, #f4e9a1 0%, #d4af37 45%, #a07d2e 100%)",
            color: "#1a1206",
            border: "1px solid rgba(255,240,180,0.45)",
            boxShadow: "0 12px 30px rgba(212,175,55,0.35), 0 1px 0 rgba(255,255,255,0.35) inset",
          }} data-testid="modal-mark-paid-submit">
            {busy ? "Saving…" : "Confirm Paid"}
          </button>
        </div>
      </div>
    </div>
  );
}
